import { ReactNode, useCallback, useEffect, useRef } from "react";
import { FCC } from "types";

interface Props {
  /** Rendered between the scroll area and the indicator, outside the scroll area. */
  trailingContent?: ReactNode;
}

/** Horizontal scroll area with the design's scroll indicator, shown only while the content overflows. */
export const ScrollIndicatorContainer: FCC<Props> = ({
  children,
  trailingContent,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);

  // Direct DOM writes: a state update per scroll event would re-render the table.
  const updateThumb = useCallback(() => {
    const container = containerRef.current;
    const track = trackRef.current;
    const thumb = thumbRef.current;

    if (!container || !track || !thumb) {
      return;
    }

    const { clientWidth, scrollLeft, scrollWidth } = container;
    track.hidden = scrollWidth <= clientWidth;
    thumb.style.width = `${(clientWidth / scrollWidth) * 100}%`;
    thumb.style.marginLeft = `${(scrollLeft / scrollWidth) * 100}%`;
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(updateThumb);
    [containerRef.current, contentRef.current].forEach(
      (element) => element && resizeObserver.observe(element),
    );
    updateThumb();

    return () => resizeObserver.disconnect();
  }, [updateThumb]);

  return (
    <div>
      <div
        className="overflow-x-auto"
        ref={containerRef}
        onScroll={updateThumb}
      >
        {/* Sized to the content, so the observer also sees the table grow.
            w-fit, not w-max: a fixed table with % columns (bills) has an
            unbounded max-content width and would grow to 1e6px. */}
        <div className="w-fit min-w-full" ref={contentRef}>
          {children}
        </div>
      </div>
      {trailingContent}
      <div
        className="mb-2.5 mt-5 h-1.5"
        ref={trackRef}
        aria-hidden="true"
        hidden
      >
        <div
          data-testid="scroll-indicator-thumb"
          className="h-full rounded-full bg-surface-secondary"
          ref={thumbRef}
        />
      </div>
    </div>
  );
};
